/**
 * Renders one list/grid browser for stored and live-linked Vault items.
 *
 * Source ownership differs behind each item, but users need consistent
 * selection, sorting, drag/drop, empty, and error behavior. Mutations remain in
 * VaultView/services so rendering cannot bypass authorization boundaries.
 */
import { useState } from 'react'
import { AlertCircle, ArrowDown, ArrowUp, CloudUpload, FolderPlus, Search, Share2, Trash2 } from 'lucide-react'
import { Button, Checkbox, TooltipProvider } from '@/components/ui'
import type { ItemResponse, VaultLayout } from '../types'
import { hasFilePayload, type VaultDragPayload } from '../lib/format'
import { VaultRow } from './VaultRow'
import { VaultGridCard } from './VaultGridCard'
import {
  sortVaultItems,
  type VaultSortDirection,
  type VaultSortKey,
} from '../lib/sort'

interface VaultBrowserProps {
  items: ItemResponse[]
  loading: boolean
  error: boolean
  accessDenied?: boolean
  view: 'folder' | 'notes' | 'shared-with-me' | 'trash' | 'search'
  canUpload: boolean
  canCreateFolder: boolean
  folderId: string
  layout: VaultLayout
  selectedItemId?: string
  selectedIds: Set<string>
  onOpen: (entry: ItemResponse) => void
  onDownload: (entry: ItemResponse) => void
  onShare: (entry: ItemResponse) => void
  onRename: (entry: ItemResponse) => void
  onMove: (entry: ItemResponse) => void
  onCopy: (entry: ItemResponse) => void
  onNewVersion: (entry: ItemResponse) => void
  onVersionHistory: (entry: ItemResponse) => void
  onDetails: (entry: ItemResponse) => void
  onTrash: (entry: ItemResponse) => void
  onRestore: (entry: ItemResponse) => void
  onDropItem: (payload: VaultDragPayload, targetFolderId: string) => void
  onDropFiles: (files: File[], targetFolderId: string) => void
  onUploadClick: () => void
  onNewFolderClick: () => void
  onRetry: () => void
  onSelectionChange: (itemId: string, selected: boolean) => void
  onSelectAll: (selected: boolean) => void
}

const EMPTY_HINTS = {
  folder: { Icon: Search, text: 'This folder is empty.' },
  notes: { Icon: Search, text: 'You have no notes yet.' },
  'shared-with-me': { Icon: Share2, text: 'Nothing has been shared with you yet.' },
  trash: { Icon: Trash2, text: 'Trash is empty.' },
  search: { Icon: Search, text: 'No items match your search.' },
} as const

function EmptyHint({ view }: { view: keyof typeof EMPTY_HINTS }) {
  const { Icon, text } = EMPTY_HINTS[view]
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-8 text-center animate-in fade-in duration-300">
      <Icon className="h-8 w-8 text-muted-foreground/60" />
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  )
}

export function VaultBrowser({
  items,
  loading,
  error,
  accessDenied = false,
  view,
  canUpload,
  canCreateFolder,
  folderId,
  layout,
  selectedItemId,
  selectedIds,
  onDropFiles,
  onUploadClick,
  onNewFolderClick,
  onRetry,
  onSelectionChange,
  onSelectAll,
  ...rowActions
}: VaultBrowserProps) {
  const [fileDragDepth, setFileDragDepth] = useState(0)
  const [sortKey, setSortKey] = useState<VaultSortKey>('updatedAt')
  const [sortDirection, setSortDirection] = useState<VaultSortDirection>('desc')
  const acceptsFiles = canUpload && view === 'folder'
  const sortedItems = sortVaultItems(items, sortKey, sortDirection)
  const selectSort = (key: VaultSortKey) => {
    if (sortKey === key) {
      setSortDirection(current => current === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection(key === 'name' ? 'asc' : 'desc')
    }
  }

  return (
    <div
      className="relative min-h-[50vh]"
      onDragEnter={(event) => {
        if (acceptsFiles && hasFilePayload(event)) setFileDragDepth((depth) => depth + 1)
      }}
      onDragLeave={(event) => {
        if (acceptsFiles && hasFilePayload(event)) setFileDragDepth((depth) => Math.max(0, depth - 1))
      }}
      onDragOver={(event) => {
        if (acceptsFiles && hasFilePayload(event)) event.preventDefault()
      }}
      onDropCapture={() => setFileDragDepth(0)}
      onDrop={(event) => {
        if (!acceptsFiles) return
        const files = Array.from(event.dataTransfer.files)
        if (!files.length) return
        event.preventDefault()
        onDropFiles(files, folderId)
      }}
    >
      {loading ? (
        <div className="space-y-1 p-4" aria-busy="true">
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="flex animate-pulse items-center gap-3 px-2 py-2.5">
              <div className="h-8 w-8 rounded-lg bg-muted" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-1/3 rounded bg-muted" />
                <div className="h-2.5 w-1/5 rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : accessDenied ? (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-8 text-center">
          <AlertCircle className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">You do not have access to this folder.</p>
        </div>
      ) : error ? (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-8 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm font-medium">This folder could not be loaded.</p>
          <Button size="sm" variant="outline" onClick={onRetry}>Try again</Button>
        </div>
      ) : items.length ? (
        <TooltipProvider delayDuration={400}>
          {layout === 'grid' ? (
            <div role="list" aria-label="Vault items" className="grid grid-cols-2 gap-3 p-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
              {sortedItems.map((entry, index) => (
                <VaultGridCard
                  key={entry.item.itemId}
                  entry={entry}
                  index={index}
                  trashView={view === 'trash'}
                  selected={selectedItemId === entry.item.itemId}
                  checked={view !== 'trash' && selectedIds.has(entry.item.itemId)}
                  showSourceBadge={view === 'search' || entry.item.metadata.source_badge === 'true'}
                  onCheckedChange={(checked) => onSelectionChange(entry.item.itemId, checked)}
                  onOpen={() => rowActions.onOpen(entry)}
                  onDetails={() => rowActions.onDetails(entry)}
                  onDownload={() => rowActions.onDownload(entry)}
                  onShare={() => rowActions.onShare(entry)}
                  onRename={() => rowActions.onRename(entry)}
                  onMove={() => rowActions.onMove(entry)}
                  onCopy={() => rowActions.onCopy(entry)}
                  onNewVersion={() => rowActions.onNewVersion(entry)}
                  onVersionHistory={() => rowActions.onVersionHistory(entry)}
                  onTrash={() => rowActions.onTrash(entry)}
                  onRestore={() => rowActions.onRestore(entry)}
                  onDropItem={rowActions.onDropItem}
                  onDropFiles={onDropFiles}
                />
              ))}
            </div>
          ) : (
            <div role="table" aria-label="Vault items" data-testid="vault-items">
              <div className="flex items-center gap-3 border-b px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <span className="flex w-8 items-center justify-center">
                  {view !== 'trash' && (
                    <Checkbox
                      checked={items.length > 0 && selectedIds.size === items.length}
                      onCheckedChange={(checked) => onSelectAll(checked === true)}
                      aria-label="Select all Vault items"
                    />
                  )}
                </span>
                <SortHeader
                  label="Name"
                  column="name"
                  active={sortKey}
                  direction={sortDirection}
                  className="flex-1 text-left"
                  onSelect={selectSort}
                />
                <SortHeader
                  label="Updated"
                  column="updatedAt"
                  active={sortKey}
                  direction={sortDirection}
                  className="hidden w-24 justify-end sm:flex"
                  onSelect={selectSort}
                />
                <SortHeader
                  label="Size"
                  column="sizeBytes"
                  active={sortKey}
                  direction={sortDirection}
                  className="hidden w-20 justify-end md:flex"
                  onSelect={selectSort}
                />
                <span className="w-36" />
              </div>
              {sortedItems.map((entry, index) => (
                <VaultRow
                  key={entry.item.itemId}
                  entry={entry}
                  index={index}
                  trashView={view === 'trash'}
                  checked={selectedIds.has(entry.item.itemId)}
                  showSourceBadge={view === 'search' || entry.item.metadata.source_badge === 'true'}
                  onCheckedChange={(checked) => onSelectionChange(entry.item.itemId, checked)}
                  onOpen={() => rowActions.onOpen(entry)}
                  onDetails={() => rowActions.onDetails(entry)}
                  onDownload={() => rowActions.onDownload(entry)}
                  onShare={() => rowActions.onShare(entry)}
                  onRename={() => rowActions.onRename(entry)}
                  onMove={() => rowActions.onMove(entry)}
                  onCopy={() => rowActions.onCopy(entry)}
                  onNewVersion={() => rowActions.onNewVersion(entry)}
                  onVersionHistory={() => rowActions.onVersionHistory(entry)}
                  onTrash={() => rowActions.onTrash(entry)}
                  onRestore={() => rowActions.onRestore(entry)}
                  onDropItem={rowActions.onDropItem}
                  onDropFiles={onDropFiles}
                />
              ))}
            </div>
          )}
        </TooltipProvider>
      ) : view === 'folder' && canUpload ? (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center animate-in fade-in duration-300">
          <div className="flex items-center gap-3">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <CloudUpload className="h-7 w-7" />
            </span>
            {canCreateFolder && (
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                <FolderPlus className="h-7 w-7" />
              </span>
            )}
          </div>
          <div>
            <p className="text-sm font-medium">Drop files anywhere to upload</p>
            {canCreateFolder && (
              <p className="text-sm text-muted-foreground">or start with an empty folder</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={onUploadClick}>
              <CloudUpload className="mr-2 h-4 w-4" /> Upload files
            </Button>
            {canCreateFolder && (
              <Button size="sm" variant="outline" onClick={onNewFolderClick}>
                <FolderPlus className="mr-2 h-4 w-4" /> New folder
              </Button>
            )}
          </div>
        </div>
      ) : (
        <EmptyHint view={view} />
      )}

      {fileDragDepth > 0 && (
        <div className="pointer-events-none absolute inset-1 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/5 backdrop-blur-[1px] animate-in fade-in zoom-in-95 duration-150">
          <div className="flex flex-col items-center gap-2 text-primary">
            <CloudUpload className="h-8 w-8 animate-bounce" />
            <p className="text-sm font-medium">Drop to upload here</p>
          </div>
        </div>
      )}
    </div>
  )
}

function SortHeader({
  label,
  column,
  active,
  direction,
  className,
  onSelect,
}: {
  label: string
  column: VaultSortKey
  active: VaultSortKey
  direction: VaultSortDirection
  className: string
  onSelect: (key: VaultSortKey) => void
}) {
  const Icon = direction === 'asc' ? ArrowUp : ArrowDown
  return (
    <button
      type="button"
      className={`items-center gap-1 hover:text-foreground ${className}`}
      onClick={() => onSelect(column)}
    >
      {label}
      {active === column && <Icon className="h-3 w-3" />}
    </button>
  )
}
